/**
 * Where a programme created for someone else to lead leaves its creator.
 *
 * @module components/programmes/ProgrammeCreatedSummary
 */

import React from "react";

import { LinkButton, PageHeader, Panel, SuccessAlert } from "@/components/ui";

export interface ProgrammeCreatedSummaryProps {
  programmeId: string;
  programmeTitle: string;
  leadName: string;
  /** The creator holds a write responsibility, so the editor opens editable. */
  canOpen: boolean;
  /** The list the creator came from. */
  backTo: string;
}

/**
 * @example
 * ```tsx
 * <ProgrammeCreatedSummary programmeId="p1" programmeTitle="BSc" leadName="Ada" canOpen={false} backTo="/admin/programmes" />
 * ```
 */
export function ProgrammeCreatedSummary({
  programmeId,
  programmeTitle,
  leadName,
  canOpen,
  backTo,
}: ProgrammeCreatedSummaryProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-4" data-testid="programme-created-summary">
      <PageHeader title="Programme created" />
      <SuccessAlert role="status">
        {programmeTitle || "The programme"} is created, with {leadName} as its lead.
      </SuccessAlert>
      <Panel title="Next steps" icon="list-checks">
        <div className="flex flex-wrap gap-2">
          <LinkButton
            to={`/programme-teams/${encodeURIComponent(programmeId)}`}
            variant="primary"
            icon="users-three"
            data-testid="programme-created-manage-team"
          >
            Manage programme team
          </LinkButton>
          {canOpen ? (
            <LinkButton
              to={`/programmes/${encodeURIComponent(programmeId)}/overview/identity`}
              variant="outline"
              icon="arrow-square-out"
              data-testid="programme-created-open"
            >
              Open programme
            </LinkButton>
          ) : null}
          <LinkButton
            to={backTo}
            variant="ghost"
            icon="arrow-left"
            data-testid="programme-created-back"
          >
            Back to programmes
          </LinkButton>
        </div>
      </Panel>
    </div>
  );
}

export default ProgrammeCreatedSummary;
